/**
 * Barra de período e de atualização dos relatórios e das listas de pedidos.
 *
 * - Período: hoje, ontem, últimos 7 dias, esta semana, semana passada, mês atual, mês passado ou
 *   datas livres. Vive na URL (`?periodo=…&de=…&ate=…`), então o link da tela leva o período junto.
 * - Atualizar agora: lê do Bling o que mudou e grava no Supabase (`bling-orders-sync`). A tela
 *   sempre lê do Supabase; o Bling só é consultado por esta carga (automática a cada hora).
 */
import { createElement as h, type ReactElement } from "react";
import { RefreshCw } from "lucide-react";
import { formatDate, todayIso } from "../finance/format.js";
import { PERIOD_OPTIONS, periodFromSearch, periodToSearch, resolvePeriod, type PeriodKey, type PeriodSelection, type ResolvedPeriod } from "../period.js";
import { useOrdersSyncState, useSyncOrdersNow } from "../queries.js";
import { Button, controlClass } from "../ui/index.js";
import { useSearchParams } from "react-router-dom";

function clock(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

export function OrdersSyncControl(): ReactElement | null {
  const state = useOrdersSyncState();
  const sync = useSyncOrdersNow();
  // Sem rota ou sem sessão de staff, não há o que oferecer: o controle some em vez de aparecer quebrado.
  if (state.isPending || !state.data) return null;
  const result = sync.data;
  const message = sync.isPending
    ? "Atualizando a partir do Bling…"
    : sync.error
      ? `Não foi possível atualizar: ${sync.error.message}`
      : result?.status === "skipped"
        ? "Já estava atualizado."
        : result
          ? `${result.processed ?? 0} pedido(s) atualizado(s).`
          : null;
  return h(
    "div",
    { className: "flex flex-wrap items-center gap-3 text-sm text-muted", "data-orders-sync": state.data.status ?? "never" },
    h("span", { title: state.data.error ?? undefined }, `Bling → Supabase: ${clock(state.data.lastSyncedAt)}${state.data.status === "error" ? " (com erro)" : state.data.status === "partial" ? " (parcial)" : ""}`),
    h(Button, { size: "sm", disabled: sync.isPending, onClick: () => sync.mutate(), "data-action": "atualizar-agora" }, h(RefreshCw, { className: "h-4 w-4", "aria-hidden": true }), sync.isPending ? "Atualizando…" : "Atualizar agora"),
    message ? h("span", { role: "status" }, message) : null
  );
}

export interface PeriodToolbarProps {
  selection: PeriodSelection;
  period: ResolvedPeriod;
  onChange: (selection: PeriodSelection) => void;
  /** Mostra o controle de atualização (some em telas sem pedidos do Bling). */
  withSync?: boolean;
}

export function PeriodToolbar({ selection, period, onChange, withSync = true }: PeriodToolbarProps): ReactElement {
  return h(
    "section",
    { "aria-label": "Período e atualização", className: "flex flex-wrap items-end justify-between gap-3 rounded-panel border border-border bg-surface p-3 shadow-s1", "data-period": `${period.from}..${period.to}` },
    h("div", { className: "flex flex-wrap items-end gap-3" },
      h("label", { className: "flex flex-col gap-1 text-xs font-bold text-muted" },
        "Período",
        h("select", {
          className: controlClass + " min-w-44",
          value: selection.key,
          "data-filter-control": "periodo",
          onChange: (event: { target: { value: string } }) => {
            const key = event.target.value as PeriodKey;
            onChange(key === "custom" ? { key, from: period.from, to: period.to } : { key });
          }
        }, PERIOD_OPTIONS.map((option) => h("option", { key: option.value, value: option.value }, option.label)))
      ),
      selection.key === "custom"
        ? h("div", { className: "flex items-end gap-2" },
            h("label", { className: "flex flex-col gap-1 text-xs font-bold text-muted" }, "De",
              h("input", { type: "date", className: controlClass, value: period.from, max: period.to, "data-filter-control": "de", onChange: (event: { target: { value: string } }) => onChange({ key: "custom", from: event.target.value, to: period.to }) })),
            h("label", { className: "flex flex-col gap-1 text-xs font-bold text-muted" }, "Até",
              h("input", { type: "date", className: controlClass, value: period.to, min: period.from, "data-filter-control": "ate", onChange: (event: { target: { value: string } }) => onChange({ key: "custom", from: period.from, to: event.target.value }) }))
          )
        : null,
      h("span", { className: "pb-2 text-sm text-muted tabular-nums" }, period.from === period.to ? formatDate(period.from) : `${formatDate(period.from)} a ${formatDate(period.to)}`)
    ),
    withSync ? h(OrdersSyncControl, null) : null
  );
}

/** Período da URL + resolução para datas; usado pelo `PeriodOrdersBoundary`. */
export function usePeriodSelection(defaultPeriod: PeriodKey): { selection: PeriodSelection; period: ResolvedPeriod; setSelection: (selection: PeriodSelection) => void } {
  const [params, setParams] = useSearchParams();
  const selection = periodFromSearch(params, defaultPeriod);
  const period = resolvePeriod(selection, todayIso());
  const setSelection = (next: PeriodSelection) => {
    // Mantém os outros parâmetros da URL (ex.: ?canal=shopee) e troca só o período.
    const merged = new URLSearchParams(params);
    for (const key of ["periodo", "de", "ate"]) merged.delete(key);
    for (const [key, value] of Object.entries(periodToSearch(next, defaultPeriod))) merged.set(key, value);
    setParams(merged, { replace: true });
  };
  return { selection, period, setSelection };
}
